import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  UserCheck, Plus, Search, LogOut as LogOutIcon, Clock, AlertTriangle,
  Phone, Car, IdCard, Trash2, X, CheckCircle2, Users,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import {
  DateChip, MultiChip, SingleChip, FilterButton, FiltersSheet, dateChipLabel,
} from './_filterChips';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PURPOSE_META = {
  guest:       { label: 'Guest',       cls: 'bg-blue-100 text-blue-700' },
  family:      { label: 'Family',      cls: 'bg-pink-100 text-pink-700' },
  delivery:    { label: 'Delivery',    cls: 'bg-orange-100 text-orange-700' },
  maintenance: { label: 'Maintenance', cls: 'bg-amber-100 text-amber-700' },
  interview:   { label: 'Interview',   cls: 'bg-purple-100 text-purple-700' },
  other:       { label: 'Other',       cls: 'bg-gray-100 text-gray-700' },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const durationSince = (iso) => {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const VisitorLog = () => {
  const { token } = useAuth();
  const [visitors, setVisitors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('in');  // in | out | all
  const [propertyFilter, setPropertyFilter] = useState('');
  const [purposeFilter, setPurposeFilter] = useState('');
  const [dateRange, setDateRange] = useState({ preset: 'all', start: '', end: '' });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (propertyFilter) params.set('property_id', propertyFilter);
      if (purposeFilter) params.set('purpose', purposeFilter);
      if (search.trim()) params.set('q', search.trim());
      if (tab === 'in') params.set('status', 'in');
      if (tab === 'out') params.set('status', 'out');
      if (dateRange.start) params.set('start_date', dateRange.start);
      if (dateRange.end) params.set('end_date', dateRange.end);
      params.set('limit', '200');

      const [vRes, sRes, pRes] = await Promise.all([
        axios.get(`${API}/visitors?${params.toString()}`, { headers }),
        axios.get(`${API}/visitors/summary${propertyFilter ? `?property_id=${propertyFilter}` : ''}`, { headers }),
        properties.length ? Promise.resolve({ data: properties }) : axios.get(`${API}/properties`, { headers }),
      ]);
      setVisitors(vRes.data.visitors || []);
      setSummary(sRes.data);
      if (!properties.length) setProperties(pRes.data || []);
    } catch (e) {
      toast({ title: 'Failed to load visitors', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, propertyFilter, purposeFilter, search, dateRange, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const checkout = async (v) => {
    try {
      await axios.post(`${API}/visitors/${v.id}/checkout`, {}, { headers });
      toast({ title: 'Visitor checked out', description: `${v.name} at ${new Date().toLocaleTimeString()}` });
      fetchData();
    } catch (e) {
      toast({ title: 'Checkout failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const removeVisitor = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/visitors/${deleting.id}`, { headers });
      toast({ title: 'Entry deleted' });
      setDeleting(null);
      fetchData();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="visitors-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <UserCheck className="text-indigo-600" /> Visitor Log
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Front-desk register — track every guest, delivery and worker walking into your property.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="visitor-add-btn">
          <Plus size={16} className="mr-2" /> Log visitor
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Currently inside" value={summary?.currently_in ?? '—'} Icon={Users} tint="indigo" testid="stat-in" />
        <StatCard label="Today" value={summary?.today_in ?? '—'} Icon={Clock} tint="emerald" testid="stat-today" />
        <StatCard label="This week" value={summary?.week_in ?? '—'} Icon={CheckCircle2} tint="blue" testid="stat-week" />
        <StatCard label="Overstay (>8h)" value={summary?.overstay ?? '—'} Icon={AlertTriangle} tint="amber" testid="stat-overstay" />
      </div>

      {/* Search + chip strip */}
      <Card className="p-4 border border-gray-200 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="visitors-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, tenant…"
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
          <SingleChip
            testId="chip-status"
            label={tab === 'in' ? 'Currently In' : tab === 'out' ? 'Checked Out' : 'All Visitors'}
            options={[
              { key: 'in', label: 'Currently In' },
              { key: 'out', label: 'Checked Out' },
              { key: 'all', label: 'All Visitors' },
            ]}
            value={tab}
            onChange={setTab}
            highlight={tab !== 'all'}
          />
          <DateChip
            testId="chip-visitor-date"
            value={dateRange}
            onChange={setDateRange}
            label={dateChipLabel(dateRange, 'Date Range')}
            highlight={dateRange.preset !== 'all' && dateRange.preset !== ''}
          />
          <SingleChip
            testId="chip-visitor-property"
            label={propertyFilter ? (properties.find(p => p.id === propertyFilter)?.name || 'Property') : 'All Properties'}
            options={[
              { key: '', label: 'All Properties' },
              ...properties.map(p => ({ key: p.id, label: p.name })),
            ]}
            value={propertyFilter}
            onChange={setPropertyFilter}
            highlight={!!propertyFilter}
          />
          <SingleChip
            testId="chip-visitor-purpose"
            label={purposeFilter ? PURPOSE_META[purposeFilter]?.label || 'Purpose' : 'All Purposes'}
            options={[
              { key: '', label: 'All Purposes' },
              ...Object.entries(PURPOSE_META).map(([k, v]) => ({ key: k, label: v.label })),
            ]}
            value={purposeFilter}
            onChange={setPurposeFilter}
            highlight={!!purposeFilter}
          />
          <FilterButton
            testId="open-visitor-filters"
            onClick={() => setSheetOpen(true)}
            count={
              (tab !== 'all' ? 1 : 0) +
              (dateRange.preset !== 'all' && dateRange.preset ? 1 : 0) +
              (propertyFilter ? 1 : 0) +
              (purposeFilter ? 1 : 0)
            }
          />
        </div>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : visitors.length === 0 ? (
        <Card className="p-10 text-center" data-testid="visitors-empty">
          <UserCheck size={40} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-600">
            {tab === 'in' ? 'No one is currently inside.' : tab === 'out' ? 'No checked-out visitors match your filters.' : 'No visitors logged yet. Tap "Log visitor" to start.'}
          </p>
        </Card>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full" data-testid="visitors-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Visitor</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Purpose</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Host</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Property</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Checked In</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Out</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visitors.map((v) => {
                const pm = PURPOSE_META[v.purpose] || PURPOSE_META.other;
                const isIn = !v.checked_out_at;
                const dur = isIn ? durationSince(v.checked_in_at) : '';
                const overstay = isIn && dur.includes('h') && parseInt(dur) >= 8;
                return (
                  <tr key={v.id} className={`border-b hover:bg-gray-50 ${overstay ? 'bg-amber-50/60' : ''}`} data-testid={`visitor-row-${v.id}`}>
                    <td className="p-3">
                      <p className="font-medium text-gray-900">{v.name}</p>
                      {v.phone && (
                        <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} /> {v.phone}</p>
                      )}
                      {v.vehicle_number && (
                        <p className="text-xs text-gray-500 flex items-center gap-1"><Car size={11} /> {v.vehicle_number}</p>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${pm.cls}`}>{pm.label}</span>
                    </td>
                    <td className="p-3 text-sm">
                      {v.tenant_name
                        ? <>
                            <p className="text-gray-900">{v.tenant_name}</p>
                            {v.room_number && <p className="text-xs text-gray-500">Room {v.room_number}</p>}
                          </>
                        : v.host_name
                          ? <span className="text-gray-700">{v.host_name}</span>
                          : <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="p-3 text-sm text-gray-700">{v.property_name || '—'}</td>
                    <td className="p-3 text-xs text-gray-700">
                      {fmtTime(v.checked_in_at)}
                      {isIn && <p className={`text-[11px] mt-0.5 ${overstay ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
                        Inside {dur}{overstay ? ' ⚠️' : ''}
                      </p>}
                    </td>
                    <td className="p-3 text-xs text-gray-700">
                      {v.checked_out_at ? fmtTime(v.checked_out_at) : (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" /> Inside
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        {isIn && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => checkout(v)}
                            data-testid={`visitor-checkout-${v.id}`}
                            title="Check out"
                            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          >
                            <LogOutIcon size={14} className="mr-1" /> Out
                          </Button>
                        )}
                        <button
                          onClick={() => setDeleting(v)}
                          data-testid={`visitor-delete-${v.id}`}
                          title="Delete entry"
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <VisitorForm
          open={showForm}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchData(); }}
          properties={properties}
          headers={headers}
        />
      )}

      {deleting && (
        <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-red-600">Delete visitor entry</DialogTitle></DialogHeader>
            <p className="text-sm text-gray-700">
              Delete <strong>{deleting.name}</strong>'s entry? This permanently removes the log record.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
              <Button onClick={removeVisitor} className="bg-red-600 hover:bg-red-700" data-testid="visitor-delete-confirm">
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <FiltersSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Visitor Filters"
        sections={[
          { key: 'tab',         label: 'Status',     kind: 'single', options: [{key:'in',label:'Currently In'},{key:'out',label:'Checked Out'},{key:'all',label:'All'}] },
          { key: 'dateRange',   label: 'Date Range', kind: 'date' },
          { key: 'propertyId',  label: 'Property',   kind: 'single', options: [{key:'',label:'All Properties'}, ...properties.map(p=>({key:p.id,label:p.name}))] },
          { key: 'purpose',     label: 'Purpose',    kind: 'single', options: [{key:'',label:'All Purposes'}, ...Object.entries(PURPOSE_META).map(([k,v])=>({key:k,label:v.label}))] },
        ]}
        values={{ tab, dateRange, propertyId: propertyFilter, purpose: purposeFilter }}
        onApply={(d) => { setTab(d.tab); setDateRange(d.dateRange); setPropertyFilter(d.propertyId); setPurposeFilter(d.purpose); }}
        onClear={() => { setTab('all'); setDateRange({preset:'all',start:'',end:''}); setPropertyFilter(''); setPurposeFilter(''); setSearch(''); }}
      />
    </div>
  );
};

const StatCard = ({ label, value, Icon, tint, testid }) => {
  const tints = {
    indigo: 'from-indigo-50 to-indigo-100 text-indigo-700',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700',
    blue: 'from-blue-50 to-blue-100 text-blue-700',
    amber: 'from-amber-50 to-amber-100 text-amber-700',
  };
  return (
    <Card className={`p-4 border-0 bg-gradient-to-br ${tints[tint] || tints.indigo}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold opacity-70">{label}</p>
          <p className="text-3xl font-bold tabular-nums mt-1">{value}</p>
        </div>
        <Icon size={22} className="opacity-60" />
      </div>
    </Card>
  );
};

// ---------- Form ----------
const VisitorForm = ({ open, onClose, onSaved, properties, headers }) => {
  const [form, setForm] = useState({
    name: '', phone: '', purpose: 'guest', property_id: properties[0]?.id || '',
    tenant_id: '', room_id: '', host_name: '', vehicle_number: '',
    id_type: '', id_number: '', notes: '',
  });
  const [tenants, setTenants] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!form.property_id) return;
    (async () => {
      try {
        const [tRes, rRes] = await Promise.all([
          axios.get(`${API}/tenants?property_id=${form.property_id}`, { headers }),
          axios.get(`${API}/properties/${form.property_id}/rooms`, { headers }).catch(() => ({ data: [] })),
        ]);
        setTenants((tRes.data || []).filter(t => t.status !== 'vacated'));
        setRooms(rRes.data || []);
      } catch (err) {
        // Tenants/rooms pre-fetch is best-effort — form still works if it fails.
        if (process.env.NODE_ENV !== 'production') {
          try { console.debug('[VisitorLog:prefetch]', err?.message || err); } catch (_) {}
        }
      }
    })();
  }, [form.property_id, headers]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast({ title: 'Name required', variant: 'destructive' });
    if (!form.property_id) return toast({ title: 'Pick a property', variant: 'destructive' });
    setSaving(true);
    try {
      const payload = { ...form };
      ['phone', 'tenant_id', 'room_id', 'host_name', 'vehicle_number', 'id_type', 'id_number', 'notes']
        .forEach(k => { if (!payload[k]) delete payload[k]; });
      await axios.post(`${API}/visitors`, payload, { headers });
      toast({ title: 'Visitor logged', description: `${form.name} checked in.` });
      onSaved();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCheck size={20} className="text-indigo-600" /> Log a visitor</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="visitor-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input data-testid="vf-name" value={form.name} onChange={e => up('name', e.target.value)} placeholder="Raj Kumar" required />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="vf-phone" value={form.phone} onChange={e => up('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="9876543210" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Purpose *</Label>
              <select data-testid="vf-purpose" value={form.purpose} onChange={e => up('purpose', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {Object.entries(PURPOSE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Property *</Label>
              <select data-testid="vf-property" value={form.property_id} onChange={e => { up('property_id', e.target.value); up('tenant_id', ''); up('room_id', ''); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required>
                <option value="">Select…</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Visiting tenant</Label>
              <select data-testid="vf-tenant" value={form.tenant_id} onChange={e => up('tenant_id', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">— not a tenant visit —</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name} · Room {t.room_number || '—'}</option>)}
              </select>
            </div>
            <div>
              <Label>Or free-text host name</Label>
              <Input data-testid="vf-host" value={form.host_name} onChange={e => up('host_name', e.target.value)} placeholder="Reception / Owner" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Vehicle no.</Label>
              <Input data-testid="vf-vehicle" value={form.vehicle_number} onChange={e => up('vehicle_number', e.target.value.toUpperCase())} placeholder="DL 01 AB 1234" />
            </div>
            <div>
              <Label>ID type</Label>
              <select data-testid="vf-idtype" value={form.id_type} onChange={e => up('id_type', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">—</option>
                <option value="aadhar">Aadhaar</option>
                <option value="pan">PAN</option>
                <option value="dl">Driving Licence</option>
                <option value="voter_id">Voter ID</option>
                <option value="passport">Passport</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>ID number</Label>
              <Input data-testid="vf-idnum" value={form.id_number} onChange={e => up('id_number', e.target.value)} placeholder="Last 4 digits is fine" />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Input data-testid="vf-notes" value={form.notes} onChange={e => up('notes', e.target.value)} placeholder="e.g., Amazon delivery — package for room 203" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="vf-submit">
              {saving ? 'Saving…' : (<><CheckCircle2 size={14} className="mr-1" /> Check in visitor</>)}
            </Button>
          </div>
          <p className="text-xs text-gray-400 flex items-center gap-1 pt-1">
            <IdCard size={11} /> Tenant &amp; room are optional — leave blank for deliveries or walk-ins.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VisitorLog;
